// src/components/H2HSection.jsx
import React from "react";

/* ── colour palette (light theme) ────────────────────────── */
const C = {
  textPrimary  : "#000",             // headings, big numbers, winner names
  textSub      : "#000",             // normal body text
  textMuted    : "#000",             // labels, dates, secondary info
  cardBg       : "#f5f5f5",          // comparison card & match cards
  scoreBadgeBg : "rgba(0,0,0,0.06)", // score pill bg
  divider      : "rgba(0,0,0,0.08)", // thin rule
  pipBg        : "#ddd",             // centre pip in mirrored bar
  green        : "#16a34a",          // home-win / home team accent
  red          : "#dc2626",          // away-win / away team accent
  greenLight   : "rgba(22,163,74,0.12)",  // W badge bg
  redLight     : "rgba(220,38,38,0.12)",  // L badge bg
  greyBadgeBg  : "rgba(0,0,0,0.07)",     // D badge bg
  greyBadgeFg  : "#000",             // D badge text
  drawBorder   : "rgba(0,0,0,0.2)",      // draw card left-border
};

/* ── helpers ─────────────────────────────────────────────── */
const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch { return iso.slice(0, 10); }
};

/* W / D / L from the perspective of the upcoming fixture's home team */
const resultOf = (match, homeName) => {
  if (match.home_score === match.away_score) return "draw";
  const ourHomeWasHome = match.home_team === homeName;
  const homeWon        = match.home_score > match.away_score;
  return (ourHomeWasHome && homeWon) || (!ourHomeWasHome && !homeWon)
    ? "home-win" : "away-win";
};

const BORDER   = { "home-win": C.green,     "away-win": C.red,       draw: C.drawBorder };
const BADGE    = { "home-win": "W",          "away-win": "L",         draw: "D" };
const BADGE_BG = { "home-win": C.greenLight, "away-win": C.redLight,  draw: C.greyBadgeBg };
const BADGE_FG = { "home-win": C.green,      "away-win": C.red,       draw: C.greyBadgeFg };

/* ── shared tiny styles ──────────────────────────────────── */
const lblStyle = { fontSize: 11, color: C.textMuted, textTransform: "uppercase", letterSpacing: 0.5 };

/* ── sub-components ──────────────────────────────────────── */

/* Mirrored bar: home ◄══ centre ══► away */
const MirroredBar = ({ homeVal, awayVal, height = 8 }) => {
  const max  = Math.max(homeVal, awayVal, 1);
  const hPct = (homeVal / max) * 100;
  const aPct = (awayVal / max) * 100;

  return (
    <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
      <div style={{ flex: 1, display: "flex", justifyContent: "flex-end", paddingRight: 6 }}>
        <div style={{ height, width: `${hPct}%`, minWidth: homeVal > 0 ? 12 : 0, background: C.green, borderRadius: 999 }} />
      </div>
      <div style={{ width: 4, height: height * 0.7, background: C.pipBg, borderRadius: 2 }} />
      <div style={{ flex: 1, paddingLeft: 6 }}>
        <div style={{ height, width: `${aPct}%`, minWidth: awayVal > 0 ? 12 : 0, background: C.red, borderRadius: 999 }} />
      </div>
    </div>
  );
};

/* Wins row */
const WinsRow = ({ homeWins, draws, awayWins }) => (
  <div style={{ marginBottom: 4 }}>
    <MirroredBar homeVal={homeWins} awayVal={awayWins} height={8} />
    <div style={{ height: 10 }} />

    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
      <span style={{ fontSize: 32, fontWeight: 700, color: C.green,    lineHeight: 1 }}>{homeWins}</span>
      <span style={{ fontSize: 20, fontWeight: 500, color: C.textMuted, lineHeight: 1 }}>{draws}</span>
      <span style={{ fontSize: 32, fontWeight: 700, color: C.red,      lineHeight: 1 }}>{awayWins}</span>
    </div>

    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
      <span style={lblStyle}>Wins</span>
      <span style={lblStyle}>Draws</span>
      <span style={lblStyle}>Wins</span>
    </div>
  </div>
);

/* Goals row */
const GoalsRow = ({ homeGoals, awayGoals, homeAvg, awayAvg }) => (
  <div style={{ marginBottom: 0 }}>
    <MirroredBar homeVal={homeGoals} awayVal={awayGoals} height={6} />
    <div style={{ height: 10 }} />

    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
      <span style={{ fontSize: 26, fontWeight: 700, color: C.textPrimary, lineHeight: 1 }}>{homeGoals}</span>
      <span style={lblStyle}>Goals</span>
      <span style={{ fontSize: 26, fontWeight: 700, color: C.textPrimary, lineHeight: 1 }}>{awayGoals}</span>
    </div>

    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
      <span style={{ fontSize: 12, color: C.textMuted }}>
        Avg <strong style={{ color: C.textPrimary }}>{homeAvg}</strong>/game
      </span>
      <span style={{ fontSize: 12, color: C.textMuted }}>
        Avg <strong style={{ color: C.textPrimary }}>{awayAvg}</strong>/game
      </span>
    </div>
  </div>
);

/* Single past-meeting card */
const MatchCard = ({ match, homeName }) => {
  const res = resultOf(match, homeName);

  const winnerName =
    match.home_score > match.away_score ? match.home_team :
    match.away_score > match.home_score ? match.away_team : null;

  return (
    <div style={{
      background: C.cardBg,
      borderRadius: 10,
      padding: "10px 14px",
      marginBottom: 6,
      borderLeft: `3px solid ${BORDER[res]}`,
    }}>
      {/* meta row: date ── comp · W/D/L */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontSize: 11, color: C.textMuted }}>{fmtDate(match.date)}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {match.competition && (
            <span style={{ fontSize: 10, color: C.textMuted, textTransform: "uppercase", letterSpacing: 0.4 }}>
              {match.competition}
            </span>
          )}
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: 0.3,
            color: BADGE_FG[res], background: BADGE_BG[res],
            padding: "2px 7px", borderRadius: 999,
          }}>{BADGE[res]}</span>
        </div>
      </div>

      {/* teams + score */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span style={{
          flex: 1, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          fontWeight: match.home_team === winnerName ? 600 : 400,
          color:      match.home_team === winnerName ? C.textPrimary : C.textSub,
        }}>{match.home_team}</span>

        <span style={{
          fontSize: 16, fontWeight: 700, color: C.textPrimary,
          background: C.scoreBadgeBg, padding: "3px 12px", borderRadius: 6,
          flexShrink: 0, letterSpacing: 1,
        }}>{match.home_score} – {match.away_score}</span>

        <span style={{
          flex: 1, textAlign: "right", fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          fontWeight: match.away_team === winnerName ? 600 : 400,
          color:      match.away_team === winnerName ? C.textPrimary : C.textSub,
        }}>{match.away_team}</span>
      </div>
    </div>
  );
};

/* ── main ────────────────────────────────────────────────── */
const H2HSection = ({ data, homeName = "Home", awayName = "Away" }) => {
  if (!data) return null;
  const { summary, recent_matches } = data;

  if (!summary || summary.total_games === 0) {
    return (
      <div style={{ padding: "40px 16px", textAlign: "center", color: C.textMuted, fontSize: 14 }}>
        No head-to-head history found between these teams.
      </div>
    );
  }

  const { total_games, home_wins, away_wins, draws, home_goals, away_goals, home_avg_goals, away_avg_goals } = summary;

  return (
    <div style={{ padding: "8px 0 24px", maxWidth: 560, margin: "0 auto" }}>

      {/* header */}
      <div style={{ textAlign: "center", marginBottom: 20 }}>
        <span style={{ fontSize: 12, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1.2 }}>
          {total_games} meeting{total_games !== 1 ? "s" : ""}
        </span>
      </div>

      {/* comparison card */}
      <div style={{ background: C.cardBg, borderRadius: 14, padding: "20px 18px 18px", marginBottom: 22 }}>
        {/* team strip */}
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: C.green }}>{homeName}</span>
          <span style={{ fontSize: 11, color: C.textMuted, textTransform: "uppercase", letterSpacing: 0.6 }}>vs</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: C.red   }}>{awayName}</span>
        </div>

        <WinsRow homeWins={home_wins} draws={draws} awayWins={away_wins} />

        <div style={{ borderTop: `1px solid ${C.divider}`, margin: "16px 0" }} />

        <GoalsRow homeGoals={home_goals} awayGoals={away_goals} homeAvg={home_avg_goals} awayAvg={away_avg_goals} />
      </div>

      {/* recent meetings */}
      {recent_matches && recent_matches.length > 0 && (
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.textMuted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10, paddingLeft: 2 }}>
            Recent Meetings
          </div>
          {recent_matches.map((m, i) => (
            <MatchCard key={i} match={m} homeName={homeName} />
          ))}
        </div>
      )}
    </div>
  );
};

export default H2HSection;
